export const UISettings = Object.freeze({
    get applicationOptions() {
        return window.SETTINGS.applicationOptions;
    },
    get defaultApplication() {
        return window.SETTINGS.defaultApplication;
    },
    get currentRegion() {
        return window.SETTINGS.currentRegion;
    },
    get regions() {
        return window.SETTINGS.regions;
    },
    get useExcel() {
        return Number(window.SETTINGS.useExcel ?? 0) === 1;
    },
    get protocol() {
        return String(window.SETTINGS.defaultLaunchProtocol ?? "");
    },
    get protocolBrowser() {
        return String(window.SETTINGS.defaultLaunchProtocolBrowser ?? "");
    },
    get availableProtocols() {
        return window.SETTINGS.launchProtocols;
    },
    get isCorp() {
        return Boolean(window.SETTINGS.isCorp);
    },
});

export const AutoLaunchSettings = Object.freeze({
    get enabled() {
        const value = window.SETTINGS.autoLaunchEnabled;
        if (value === undefined || value === "") {
            return false;
        }
        return Number(value) === 1;
    },
    get appName() {
        return window.SETTINGS.autoLaunchAppName || window.SETTINGS.defaultApplication;
    },
    get appDisplay() {
        return window.SETTINGS.autoLaunchAppDisplay || window.SETTINGS.defaultApplication;
    },
    get protocol() {
        return window.SETTINGS.autoLaunchType || window.SETTINGS.defaultLaunchProtocol;
    },
    get launchExcel() {
        const value = window.SETTINGS.autoLaunchExcel;
        if (value === undefined || value === "") {
            return false;
        }
        return Number(value) === 1;
    },
});

export const SessionSettings = Object.freeze({
    get expiration() {
        let timeoutSeconds = Number(window.SETTINGS.expirationSeconds ?? "0");

        return new Date(Date.now() + timeoutSeconds * 1000);
    },
    get expirationCheckInterval() {
        // TODO: Pull from configuration
        return 5000;
    },
});

export const LaunchStatusCheckSettings = Object.freeze({
    // TODO: Pull hardcoded values from configuation
    get enabled() {
        return true;
    },
    get interval() {
        return 5000;
    },
    get timeout() {
        return 90000;
    },
});

export const ClientVersionCheckSettings = Object.freeze({
    get enabled() {
        return window.SETTINGS.clientVersionCheck?.enabled === true;
    },
    get outdatedWarningEnabled() {
        return window.SETTINGS.clientVersionCheck?.outdatedWarningEnabled === true;
    },
    get interval() {
        return Number(window.SETTINGS.clientVersionCheck?.pollIntervalMs ?? 3000);
    },
    get timeout() {
        return Number(window.SETTINGS.clientVersionCheck?.pollTimeoutMs ?? 30000);
    },
});

// ua-parser's OS family strings, as passed through by flask_helpers.get_os_family().
const OsFamilies = Object.freeze({
    Mac: "Mac OS X",
    Windows: "Windows",
});

export const UserInformation = Object.freeze({
    get name() {
        return window.SETTINGS.name;
    },
    get location() {
        return window.SETTINGS.location;
    },
    get osFamily() {
        return String(window.SETTINGS.osFamily ?? "");
    },
});

// Mirrors PHASE_* in controllers/common.py.
export const MigrationPhases = Object.freeze({
    None: 0,
    OptIn: 1,
    Migrated: 2,
    // Migrated, minus the announcement banner.
    Settled: 3,
});

// Migrated or Settled; prefer this over === Migrated.
export const hasMigrated = (phase) =>
    phase === MigrationPhases.Migrated || phase === MigrationPhases.Settled;

export const MigrationSettings = Object.freeze({
    get phase() {
        return Number(window.SETTINGS.migrationPhase ?? MigrationPhases.None);
    },

    get isFirstVisit() {
        return Number(window.SETTINGS.isFirstVisit ?? 0) === 1;
    },

    get message() {
        return String(window.SETTINGS.migrationMessage ?? "");
    },

    get title() {
        return String(window.SETTINGS.migrationTitle ?? "");
    },

    // True when the last confirmed active session used Parallels.
    get parallelsPreview() {
        return Number(window.SETTINGS.parallelsPreview ?? 0) === 1;
    },
});

// Managed Macs get their own "special" experience.
export const isManagedMac = () =>
    UISettings.isCorp &&
    UserInformation.osFamily === OsFamilies.Mac &&
    MigrationSettings.phase !== MigrationPhases.None;

export const ClientVersionSettings = Object.freeze({
    get isOutdated() {
        return Boolean(window.SETTINGS.clientVersionInfo?.isOutdated);
    },
    get installedVersion() {
        return String(window.SETTINGS.clientVersionInfo?.installedVersion ?? "");
    },
    get latestVersion() {
        return String(window.SETTINGS.clientVersionInfo?.latestVersion ?? "");
    },
    get minVersion() {
        return String(window.SETTINGS.clientVersionInfo?.minVersion ?? "");
    },
});

export const Settings = Object.freeze({
    get ui() {
        return UISettings;
    },
    get autoLaunch() {
        return AutoLaunchSettings;
    },
    get session() {
        return SessionSettings;
    },
    get launchStatusCheck() {
        return LaunchStatusCheckSettings;
    },
    get clientVersionCheck() {
        return ClientVersionCheckSettings;
    },
    get userInfo() {
        return UserInformation;
    },
    get migration() {
        return MigrationSettings;
    },
    get clientVersion() {
        return ClientVersionSettings;
    },
});
